import { type FC, useState, MouseEvent } from "react";
import {
  HealthFactorCircle,
  CustomDropdown,
  LoanImage,
  CtaPrimary,
} from "../..";
import { ModalInstantSell, ModalRedeem } from "../../modals";
import React from "react";
import classNames from "classnames";
import {
  useDetectToggleModal,
  usePromiseProperty,
  usePromisePropertyV2,
} from "../../../logic/hooks";
import { FaChevronUp } from "react-icons/fa";
import { NftLoanDetail } from "./NftLoanDetail";
import useModalHelper from "../../modals/hooks/useModalHelper";
import displayHealthFactor from "../../../logic/helpers/displayHealthFactor";
import { Loan } from "../../../logic/types/loan/Loan";
import { Address } from "viem";
import InAuctionTag from "./InAuctionTag";
import TokenIcon from "components/icons/TokenIcon";
import { INft } from "logic/types/nft/INft";
import { BasicProps } from "components/types/BasicProps";
import {
  useDropdownOptions,
  useListForSale,
  useLoanIdToShow,
} from "logic/hooks/borrow";

type Props = BasicProps & {
  loan: Loan;
  onRedeemed: (loanId: Address) => void;
  onModalClose?: () => void;
  onModalOpen?: (loanId: Address) => void;
  availableNfts: INft[];
  onNftUnlisted?: (loanId: Address, nft: INft) => void;
  onSold?: (loanId: Address, nft: INft, amountRepaid?: bigint) => void;
};

export const MyLoan: FC<Props> = (props) => {
  const {
    className,
    loan,
    onModalClose,
    onModalOpen,
    availableNfts,
    onRedeemed,
    onSold,
    onNftUnlisted,
  } = props;
  const [isNftListOpen, setNftListOpen] = useState<boolean>(false);
  const [
    isModalInstantSellOpen,
    toggleModalInstantSell,
    launcherModalInstantSell,
  ] = useModalHelper();
  const [isModalRedeemOpen, toggleModalRedeem, launcherModalRedeem] =
    useModalHelper();

  const valuation = usePromiseProperty("valuation", loan);
  const liquidationPrice = usePromiseProperty("liquidationPrice", loan);
  const { value: healthFactor, updateValue: updateHf } = usePromisePropertyV2(
    "healthFactor",
    loan
  );
  const { value: currentBorrowAmount, updateValue: updateCurrBorrowAm } =
    usePromisePropertyV2("currentBorrowAmount", loan);
  const { value: availableToBorrow, updateValue: updateAvToBorrow } =
    usePromisePropertyV2("availableToBorrow", loan);
  const { value: isAuctioned, updateValue: updateIsAuctioned } =
    usePromisePropertyV2("isAuctioned", loan, false);
  const auctionFinished = usePromiseProperty("auctionFinished", loan, false);

  const { handleListClick, ctaLiteral } = useListForSale(
    loan,
    loan.nfts[0],
    onNftUnlisted
  );

  const loanIdValueToShow = useLoanIdToShow(loan);

  useDetectToggleModal(
    [isModalInstantSellOpen],
    () => onModalOpen?.(loan.id),
    () => onModalClose?.()
  );

  const dropdownOptions = useDropdownOptions(
    loan,
    availableNfts.length > 0,
    launcherModalInstantSell,
    ctaLiteral,
    handleListClick
  );

  const handleRepayButton = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    launcherModalRedeem();
  };

  const toggleNftList = () => {
    loan.nfts.length > 1 && setNftListOpen(!isNftListOpen);
  };

  const handleRedeemed = (loanId: Address) => {
    updateAvToBorrow();
    updateCurrBorrowAm();
    updateHf();
    updateIsAuctioned();

    onRedeemed(loanId);
  };

  return (
    <>
      <tr
        className={classNames(
          "bg-secondary hover:bg-primary/10 text-base font-medium",
          className,
          loan.isMulticollateral && "cursor-pointer"
        )}
        onClick={toggleNftList}
      >
        <td
          scope="row"
          className={classNames(
            `flex items-center gap-3 py-4 pl-1 pr-3 font-medium`,
            loan.nfts.length == 0
              ? "ml-[30px]"
              : loan.nfts.length > 1
              ? "mt-1.5"
              : ""
          )}
        >
          {loan.nfts.length > 1 ? (
            <FaChevronUp
              className={classNames(
                "transition-transform duration-200 transform",
                isNftListOpen ? "rotate-180" : "rotate-0"
              )}
              width={18}
              height={11}
            />
          ) : (
            <div className="w-[18px] h-[11px]"></div>
          )}

          <LoanImage
            className="w-16 h-16 rounded-md"
            nfts={loan.nfts.slice(0, 4)}
          />
        </td>

        <td className="px-3 font-bold">{loanIdValueToShow}</td>
        <td className="px-3 items-center">
          <div className="flex gap-x-2 items-center">
            {valuation !== undefined && loan.currency.formatAmount(valuation)}
            <TokenIcon className="w-4 h-4" currency={loan.currency} />
          </div>
        </td>
        <td className="px-3">
          <div className="flex gap-x-2 items-center">
            {liquidationPrice !== undefined &&
              loan.currency.formatAmount(liquidationPrice)}

            <TokenIcon className="w-4 h-4" currency={loan.currency} />
          </div>
        </td>
        <td className="py-4 px-3">
          <div className="flex items-center gap-1">
            <p>
              {healthFactor !== undefined && displayHealthFactor(healthFactor)}
            </p>
            {healthFactor !== undefined && (
              <HealthFactorCircle healthFactor={healthFactor} />
            )}
          </div>
        </td>
        <td className="py-4 px-3">
          <div className="relative sm:max-w-xl sm:mx-auto mt-0.5">
            <div className="group flex relative border-gray-400 text-center">
              <div className="flex gap-x-2 items-center">
                {currentBorrowAmount !== undefined &&
                  loan.currency.formatAmount(currentBorrowAmount!)}

                <TokenIcon className="w-4 h-4" currency={loan.currency} />
              </div>
              <div className="opacity-0 max-w-max ml-14 mb-2 bg-secondary text-white border border-white text-center text-xs rounded-lg py-2 absolute z-10 group-hover:opacity-100 bottom-full -left-1/2 px-3 pointer-events-none">
                {currentBorrowAmount !== undefined &&
                  loan.currency.formatAmount(currentBorrowAmount!)}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3">
          <div className="flex gap-x-2 items-center">
            {availableToBorrow !== undefined &&
              loan.currency.formatAmount(availableToBorrow!)}
            <div className="bg-white w-4 h-4 rounded-full">
              <TokenIcon className="w-4 h-4" currency={loan.currency} />
            </div>
          </div>
        </td>
        <td className="px-3">
          {isAuctioned && <InAuctionTag className="w-40" />}
        </td>
        <td className="px-3">
          {isAuctioned ? (
            <CtaPrimary
              className="w-40"
              onClick={handleRepayButton}
              disabled={auctionFinished}
            >
              Repay
            </CtaPrimary>
          ) : (
            <CustomDropdown
              className="w-40"
              label="Manage"
              options={dropdownOptions}
              onClick={(event) => event.stopPropagation()}
            />
          )}
        </td>
      </tr>
      {loan.nfts.length > 1 &&
        isNftListOpen &&
        loan.nfts.map((nft, index) => (
          <NftLoanDetail
            className={
              index === 0
                ? "border-t-2 border-b"
                : index + 1 === loan.nfts.length
                ? "border-t border-b-2"
                : "border-t border-b"
            }
            loan={loan}
            key={nft.alt}
            nft={nft}
            onSold={onSold}
            onNftUnlisted={onNftUnlisted}
          />
        ))}
      {isModalRedeemOpen && isAuctioned && (
        <ModalRedeem
          onRedeemed={handleRedeemed}
          isOpen={isModalRedeemOpen}
          toggleModal={toggleModalRedeem}
          item={loan}
        />
      )}
      {isModalInstantSellOpen && (
        <ModalInstantSell
          isOpen={isModalInstantSellOpen}
          toggleModal={toggleModalInstantSell}
          loan={loan}
          nft={loan.nfts[0]}
          onSold={onSold}
        />
      )}
    </>
  );
};

export const cancelListSucceededLiteral = "Your debt was delist successfully";

export const cancelListLoadingLiteral =
  "Your debt is being delist on chain. Please wait.";
